import { Directive, Input, OnDestroy, OnInit, TemplateRef, ViewContainerRef } from '@angular/core'
import { isArray } from 'lodash'
import { first, map, Subject, takeUntil } from 'rxjs'

import { Clinic } from '../model'
import { ClinicService } from '../services/clinic.service'

function hasFeature(clinic: Clinic, feature: string) {
  const supports = clinic.integration?.supports ?? []

  if (supports.includes(feature)) {
    // if this is an appointment field, look to see if the field is enabled
    if (feature.startsWith('UPDATE_APT_')) {
      const updateAppointmentFields = clinic.settings.updateAppointmentFields ?? []
      return updateAppointmentFields?.includes(feature)
    }
    return true
  } else {
    return false
  }
}

@Directive({
  selector: '[mmxHasIntegrationSupport]',
  standalone: false,
})
export class HasIntegrationSupportDirective implements OnInit, OnDestroy {
  @Input()
  public mmxHasIntegrationSupport: string | string[]

  @Input()
  public mmxHasIntegrationSupportElse: TemplateRef<any> | null = null

  /**
   * To use this input, you need to pass it as an option in the directive selector:
   *
   * <div *mmxHasIntegrationSupport="['UPDATE_APT', 'UPDATE_APT_RESCHEDULE']; operator: 'AND'">
   */
  @Input()
  public mmxHasIntegrationSupportOperator: 'AND' | 'IN' | 'OR' = 'AND'

  private onDestroy$ = new Subject<void>()

  constructor(
    private templateRef: TemplateRef<any>,
    private viewContainer: ViewContainerRef,
    private clinicService: ClinicService,
  ) { }

  public ngOnInit() {
    this.clinicService.clinic$.pipe(
      takeUntil(this.onDestroy$),
      first(),
      map(clinic => {
        const features = isArray(this.mmxHasIntegrationSupport) ? this.mmxHasIntegrationSupport : [this.mmxHasIntegrationSupport]
        const operator = this.mmxHasIntegrationSupportOperator
        // const patientDemographicFields = clinic.settings.patientDemographicFields
        return operator === 'AND'
          ? features.every(feature => hasFeature(clinic, feature))
          : features.some(feature => hasFeature(clinic, feature))
      }),
    ).subscribe((hasFeatures) => this.handleVisibility(hasFeatures))
  }

  public ngOnDestroy() {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  private handleVisibility(hasPermission: boolean) {
    this.viewContainer.clear()

    if (hasPermission) {
      return this.viewContainer.createEmbeddedView(this.templateRef)
    }

    if (this.mmxHasIntegrationSupportElse) {
      this.viewContainer.createEmbeddedView(this.mmxHasIntegrationSupportElse)
      return
    }
  }
}
